import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/AppShell';
import { Thermometer } from '../components/Thermometer';
import PastDueBanner from '../components/PastDueBanner';
import FounderWMMSetupChecklist from '../components/FounderWMMSetupChecklist';
import { Button } from '../components/ui/button';
import { TrendingUp, BookOpen, Crown, Users, Sparkles, Youtube, Calendar, Settings, Award, Play, IdCard, ExternalLink, PartyPopper } from 'lucide-react';
import OnboardingModal from '../components/OnboardingModal';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, logout, loading: authLoading, refreshUser, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [potData, setPotData] = useState({ total_amount: 0, goal_amount: 1000000 });
  const [userSales, setUserSales] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [listingsStats, setListingsStats] = useState(null);
  const [downline, setDownline] = useState([]);
  const [wemmCardUrl, setWemmCardUrl] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchDashboardData();
      fetchOnboarding();
    }
  }, [user, authLoading]);

  const fetchOnboarding = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/onboarding/status`, { headers: getAuthHeader() });
      setOnboarding(res.data);
      // Never auto-open during an admin impersonation session — the dialog
      // overlay would block the "Return to Admin" banner button.
      if (res.data.should_show && !sessionStorage.getItem('impersonation_user')) {
        // Don't stack on top of the legal-acceptance overlay a brand-new
        // account sees — wait for it to finish, then open.
        try {
          const legal = await axios.get(`${API_URL}/api/legal/pending-acceptance`, { headers: getAuthHeader() });
          if ((legal.data.pending || []).length > 0) {
            window.addEventListener(
              'legal-acceptance-complete',
              () => setShowOnboarding(true),
              { once: true },
            );
            return;
          }
        } catch {
          /* fall through and show */
        }
        setShowOnboarding(true);
      }
    } catch {
      // Non-fatal — dashboard renders without the onboarding banner.
    }
  };

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      const [potRes, leaderboardRes] = await Promise.all([
        axios.get(`${API_URL}/api/pot/status`),
        axios.get(`${API_URL}/api/leaderboard/founders`)
      ]);

      setPotData(potRes.data);
      setLeaderboard(leaderboardRes.data);

      // Fetch listings stats if user has a membership
      if (user?.membership_tier !== 'none') {
        try {
          const listingsRes = await axios.get(`${API_URL}/api/listings/my-stats`, { headers });
          setListingsStats(listingsRes.data);
        } catch (e) {
          console.log('Listings stats not available');
        }
      }

      if (user?.membership_tier === 'founder') {
        const salesRes = await axios.get(`${API_URL}/api/sales`, { headers });
        setUserSales(salesRes.data);
      }

      // Fetch downline for Growth and Founder members
      if (['growth', 'founder'].includes(user?.membership_tier)) {
        try {
          const downlineRes = await axios.get(`${API_URL}/api/my-downline`, { headers });
          setDownline(downlineRes.data);
        } catch (e) {
          console.log('Downline not available');
        }
      }

      // Resolve the user's WeMillionairesMM card URL (Founders only).
      try {
        const cardRes = await axios.get(`${API_URL}/api/wemm/my-card-url`, { headers });
        if (cardRes.data?.eligible && cardRes.data?.url) {
          setWemmCardUrl(cardRes.data.url);
        }
      } catch (e) {
        // Silently skip — endpoint is optional.
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const totalProfit = (user.total_profit_earned || listingsStats?.total_profit_earned || 0);
  const totalResidual = (user.total_residual_earned || listingsStats?.total_residual_earned || 0);
  const totalCommission = totalProfit + totalResidual + (user.total_downline_commission || 0);
  const tierLabel = user.membership_tier === 'none' ? 'No membership' : user.membership_tier;

  return (
    <AppShell active="dashboard" title="Dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-root">
        <PastDueBanner />

        {/* First-login onboarding — welcome modal + resume banner for new
            accounts. Older accounts keep the classic WMM checklist. */}
        <OnboardingModal
          open={showOnboarding}
          onOpenChange={setShowOnboarding}
          status={onboarding}
          setStatus={setOnboarding}
          getAuthHeader={getAuthHeader}
        />
        {onboarding && onboarding.eligible && !onboarding.completed && (
          <div
            className="mb-6 flex items-center justify-between gap-3 rounded-xl px-4 py-3 bg-brand-gold/10 border border-brand-gold/25"
            data-testid="onboarding-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <PartyPopper className="w-5 h-5 text-brand-gold flex-shrink-0" />
              <p className="text-sm text-white truncate">
                {onboarding.is_founder ? 'Founder onboarding' : 'Getting started'}:{' '}
                <span className="text-brand-gold font-semibold">
                  {onboarding.completed_count} of {onboarding.total_steps} complete
                </span>
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowOnboarding(true)}
              className="bg-brand-gold text-black hover:brightness-110 text-xs flex-shrink-0"
              data-testid="onboarding-resume"
            >
              Resume
            </Button>
          </div>
        )}
        {!(onboarding && onboarding.eligible) && (
          <div className="mb-6">
            <FounderWMMSetupChecklist getAuthHeader={getAuthHeader} />
          </div>
        )}

        {/* Cinematic welcome header */}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-playfair text-3xl md:text-4xl text-white mb-1" data-testid="dashboard-welcome">
              Welcome back, {user.name?.split(' ')[0] || 'Member'}
            </h1>
            <p className="text-sm text-gray-400">Here&apos;s what&apos;s happening with your account today.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border ${
              user.membership_tier === 'founder' ? 'bg-brand-gold/15 text-brand-gold border-brand-gold/30' :
              user.membership_tier === 'growth' ? 'bg-brand-blue/20 text-blue-300 border-brand-blue/30' :
              user.membership_tier === 'ascend' ? 'bg-slate-500/20 text-slate-300 border-slate-500/30' :
              'bg-white/5 text-gray-400 border-white/10'
            }`} data-testid="tier-badge">
              {user.membership_tier === 'founder' && <Crown className="w-3 h-3 inline mr-1" />}
              {tierLabel}
            </span>
            {user.membership_tier === 'founder' && user.pending_founder_approval && (
              <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-1 rounded border border-amber-500/30">Pending Approval</span>
            )}
            {user.membership_tier === 'none' && (
              <Link to="/pricing">
                <Button size="sm" className="bg-brand-gold text-black hover:brightness-110 font-semibold" data-testid="upgrade-membership-btn">
                  Upgrade
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Cinematic 4-KPI grid — mockup dashboard.html style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="kpi-commission">
            <p className="text-xs text-gray-400 mb-1 uppercase tracking-wider">Total Commission</p>
            <p className="text-3xl font-light text-white">${totalCommission.toLocaleString()}</p>
            <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> {(user.referral_earnings || 0) > 0 ? '+' : ''}${(user.referral_earnings || 0).toLocaleString()} referrals
            </p>
            <p className="text-[10px] text-gray-500 mt-1" data-testid="commission-source-caption">From Directory listing sales — subscriptions don't earn commission</p>
            <Link to="/statements" className="text-xs text-brand-gold hover:underline mt-1 inline-block" data-testid="view-statements-link">View statements →</Link>
          </div>
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="kpi-listings">
            <p className="text-xs text-gray-400 mb-1 uppercase tracking-wider">Directory Listings</p>
            <p className="text-3xl font-light text-white">{(user.listings_available || listingsStats?.listings_available || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-2">of {(user.listings_total || listingsStats?.listings_total || 0).toLocaleString()} from your tier — updates as listing sales are verified</p>
            <p className="text-xs text-emerald-400 mt-1" data-testid="service-listings-unlimited">Service listings: Unlimited</p>
          </div>
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="kpi-progress">
            <p className="text-xs text-gray-400 mb-1 uppercase tracking-wider">Pot Progress</p>
            <p className="text-3xl font-light text-white">{potData.goal_amount ? Math.round((potData.total_amount / potData.goal_amount) * 100) : 0}%</p>
            <div className="w-full h-1.5 bg-white/10 rounded-full mt-3">
              <div className="h-full bg-brand-gold rounded-full" style={{ width: `${potData.goal_amount ? Math.min(100, (potData.total_amount / potData.goal_amount) * 100) : 0}%` }} />
            </div>
          </div>
          <Link to={user.membership_tier === 'none' ? '/pricing' : '/memberships'} className="block">
            <div className="bg-gradient-to-br from-brand-blue/20 to-brand-surface border border-brand-blue/30 rounded-2xl p-6 relative overflow-hidden group cursor-pointer hover:border-brand-blue transition-colors h-full" data-testid="kpi-tier">
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-brand-blue/20 rounded-full blur-2xl" />
              <p className="text-sm text-brand-blue font-medium mb-1 uppercase tracking-wider text-xs">Current Tier</p>
              <p className="text-2xl font-playfair text-white capitalize">{tierLabel}</p>
              <p className="text-xs text-gray-400 mt-2">
                {user.membership_tier === 'founder' ? 'You are at the top tier · 25% listing-sale residuals' :
                 user.membership_tier === 'growth' ? 'Upgrade to Founder for 25% listing-sale residuals + downline' :
                 user.membership_tier === 'ascend' ? 'Upgrade to Growth for team building' :
                 'Join a tier to unlock earnings & content'}
              </p>
            </div>
          </Link>
        </div>

        {/* Action toolbar — quick links */}
        <div className="flex items-center gap-2 mb-8 flex-wrap">
          {['growth', 'founder'].includes(user.membership_tier) && (
            <>
              <Link to="/ai-video-builder">
                <Button size="sm" variant="outline" className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10" data-testid="ai-video-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> AI Video Builder
                </Button>
              </Link>
              <Link to="/youtube-videos">
                <Button size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="youtube-btn">
                  <Youtube className="w-4 h-4 mr-2" /> My Videos
                </Button>
              </Link>
            </>
          )}
          {user.membership_tier !== 'none' && (
            <>
              <Link to="/scheduler">
                <Button size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="scheduler-btn">
                  <Calendar className="w-4 h-4 mr-2" /> Scheduler
                </Button>
              </Link>
              <Link to="/my-certificates">
                <Button size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="certificates-btn">
                  <Award className="w-4 h-4 mr-2" /> Certificates
                </Button>
              </Link>
              <Link to={`/member/${user.id}`} target="_blank">
                <Button size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="microsite-btn">
                  <Users className="w-4 h-4 mr-2" /> My Microsite
                </Button>
              </Link>
              <Link to="/profile/settings">
                <Button size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="settings-btn">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Main grid — Continue Learning + Upcoming/Leaderboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Continue Learning (cinematic hover thumbnail) */}
          <div className="md:col-span-2 bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="continue-learning">
            <h2 className="text-lg font-playfair mb-4 flex items-center gap-2">
              <Play className="w-4 h-4 text-brand-gold" /> Continue Learning
            </h2>
            {user.membership_tier === 'none' ? (
              <div className="text-center py-8">
                <p className="text-gray-400 mb-4 text-sm">Upgrade your membership to access exclusive courses</p>
                <Link to="/pricing">
                  <Button className="bg-brand-gold text-black hover:brightness-110" data-testid="upgrade-for-content-btn">View Memberships</Button>
                </Link>
              </div>
            ) : (
              <Link to="/courses" className="flex gap-4 group cursor-pointer" data-testid="browse-courses-btn">
                <div className="w-48 h-32 bg-gray-800 rounded-lg overflow-hidden relative flex-shrink-0">
                  <div className="w-full h-full bg-gradient-to-br from-brand-gold/20 to-brand-blue/20 opacity-80 group-hover:scale-105 transition-transform duration-500 flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-brand-gold/60" />
                  </div>
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                      <Play className="w-5 h-5 fill-current" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  <span className="text-xs text-brand-gold font-medium uppercase tracking-wider mb-1">Masterclass Library</span>
                  <h3 className="text-xl font-playfair mb-2">Browse Courses</h3>
                  <p className="text-sm text-gray-400 line-clamp-2">Cinematic training from top members. Watch, learn, apply.</p>
                </div>
              </Link>
            )}
          </div>

          {/* Founder Leaderboard */}
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="leaderboard-card">
            <h2 className="text-lg font-playfair mb-4 flex items-center gap-2">
              <Crown className="w-4 h-4 text-brand-gold" /> Founder Leaderboard
            </h2>
            {leaderboard.length > 0 ? (
              <div className="space-y-3">
                {leaderboard.slice(0, 5).map((member, index) => (
                  <div key={member.id} className="flex items-center gap-3" data-testid={`leaderboard-member-${index}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-yellow-900' :
                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-900' :
                      index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-800 text-amber-100' :
                      'bg-white/5 text-gray-400'
                    }`}>{index + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{member.name}</p>
                      <p className="text-xs text-brand-gold font-mono">${member.sales_total.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4 text-sm">No founders yet</p>
            )}
          </div>
        </div>

        {/* Referral & Downline row */}
        <div id="downline" className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 scroll-mt-24">
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="referral-card">
            <h3 className="text-lg font-playfair mb-3">Your Referral Code</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-mono font-bold text-brand-gold">{user.referral_code}</p>
                <p className="text-xs text-gray-400 mt-1">Share this code to earn commissions</p>
              </div>
              <Button
                onClick={() => { navigator.clipboard.writeText(user.referral_code); toast.success('Referral code copied!'); }}
                variant="outline"
                className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                data-testid="copy-referral-btn"
              >Copy</Button>
            </div>
            {user.referral_earnings > 0 && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-xs text-gray-400">Total earnings: <span className="text-brand-gold font-semibold">${user.referral_earnings.toLocaleString()}</span></p>
              </div>
            )}

            {/* Founder-only: link to their WeMillionairesMM digital card */}
            {wemmCardUrl && (
              <a
                href={wemmCardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-between gap-3 p-3 rounded-xl border border-brand-gold/30 bg-gradient-to-r from-brand-gold/10 to-transparent hover:from-brand-gold/20 transition-colors group"
                data-testid="wemm-card-link"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-gold/20 flex items-center justify-center">
                    <IdCard className="w-5 h-5 text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Your WeMasterminds Card</p>
                    <p className="text-[11px] text-gray-400">Digital business card · opens WeMillionairesMM</p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-brand-gold group-hover:translate-x-0.5 transition-transform" />
              </a>
            )}
          </div>

          {/* Downline */}
          {['growth', 'founder'].includes(user?.membership_tier) && (
            <div className="bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="downline-card">
              <h3 className="text-lg font-playfair mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-brand-blue" /> My Downline
              </h3>
              {downline.length > 0 ? (
                <div className="space-y-2">
                  {downline.slice(0, 4).map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-2 rounded hover:bg-white/5" data-testid="downline-member">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{member.name}</p>
                        <p className="text-xs text-gray-400 capitalize">{member.membership_tier}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${member.subscription_status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-500/15 text-slate-400'}`}>
                        {member.subscription_status}
                      </span>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 text-center mt-2">{downline.length} member{downline.length !== 1 ? 's' : ''} recruited</p>
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center py-4">Share your referral code to grow your team!</p>
              )}
            </div>
          )}
        </div>

        {/* Thermometer */}
        <div className="bg-brand-surface border border-white/5 rounded-2xl p-6 mb-8" data-testid="thermometer-card">
          <Thermometer current={potData.total_amount} goal={potData.goal_amount} />
        </div>

        {/* Founder-specific — sales & downline detailed */}
        {user.membership_tier === 'founder' && (
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-6 mb-8" data-testid="founder-sales">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-playfair">Your Sales</h3>
              {user.is_admin && (
                <Link to="/sales">
                  <Button size="sm" className="bg-brand-gold text-black hover:brightness-110" data-testid="submit-sale-btn">Submit New Sale</Button>
                </Link>
              )}
            </div>
            {userSales.length > 0 ? (
              <div className="space-y-2">
                {userSales.slice(0, 5).map((sale) => (
                  <div key={sale.id} className="p-3 rounded-lg bg-brand-surface2 border border-white/5 flex justify-between items-center" data-testid="sale-item">
                    <div>
                      <p className="font-semibold text-white">${sale.amount.toLocaleString()}</p>
                      <p className="text-xs text-gray-400">{sale.description}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      sale.status === 'verified' ? 'bg-emerald-500/15 text-emerald-400' :
                      sale.status === 'rejected' ? 'bg-rose-500/15 text-rose-400' :
                      'bg-amber-500/15 text-amber-400'
                    }`}>{sale.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-6 text-sm">No sales submitted yet</p>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
